import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@zenbusiness/application-commons-graphql';
import { WebView } from '@zenbusiness/application-commons-webview';
import Button from '@zenbusiness/zui/Button';
import Heading from '@zenbusiness/zui/Heading';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import PropTypes from 'prop-types';

import EmptyPayment from '#client/components/EmptyPayment';
import FilterOptions from '#client/components/FilterOptions';
import MobileDrawer from '#client/components/MobileDrawer';
import NoResultTransaction from '#client/components/NoResultTransaction';
import PageTitle from '#client/components/PageTitle';
import SelectInput from '#client/components/SelectInput';
import TransactionSkeleton from '#client/components/TransactionListSkeleton';
import { FAQ_CHECK_HISTORY_URL } from '#client/constants/FAQs';
import { PAGINATION_DEFAULT_LIMIT } from '#client/constants/pagination';
import { RENDER_TRANSACTION_TYPE, checkDepositStatusOptions } from '#client/constants/transaction';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useCheckDepositModal from '#client/hooks/useCheckDepositModal';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import { useLogError } from '#client/hooks/useLogError';
import useMemoObject from '#client/hooks/useMemoObject';
import useSkeleton from '#client/hooks/useSkeleton';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { GET_LIST_CHECK_DEPOSIT_QUERY } from '#client/queries/checkDeposit';
import { getDateRange, getMonthSelectOptions } from '#client/utils/date';
import { checkIsMobile } from '#client/utils/devices';
import { getOptionName } from '#client/utils/transaction';

import { DialogContentStyled, OptionButtonStyle } from './CheckDepositContainer.styles';
import ListCheckDeposit from './ListCheckDeposit';

import { getStatusPayload } from '../utils/status';

const MONTH = 'month';
const STATUS = 'status';

const initFilter = {
  [MONTH]: '',
  [STATUS]: ''
};

const monthOptions = getMonthSelectOptions();

/**
 * CheckDepositContainer
 *
 * @param {object} payload - { handleClose, openOnPage }
 * @param {string} payload.handleClose - only exists if the component render on the modal
 * @param {string} payload.openOnPage - the flag to know that the component in render on the page
 * @returns {React.ReactNode} : Component
 */
export default function CheckDepositContainer({ handleClose = null, openOnPage = false }) {
  const { isWebView } = WebView;
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { unitAccountId } = useUnitAccountId();
  const [showMonthFilter, setShowMonthFilter] = useState(false);
  const [showStatusFilter, setShowStatusFilter] = useState(false);

  const [filters, setFilters] = useState(initFilter);
  const [offset, setOffset] = useState(0);
  const [isClickLoadMore, setIsClickLoadMore] = useState(false);
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  const { createCommonErrorToast } = useToastContext();
  const openCheckDepositModal = useCheckDepositModal();
  const updateOriginalTransactions = (prevData, data) => {
    const checkDepositList = isClickLoadMore
      ? [...prevData.checkDeposits.checkDeposits, ...data.checkDeposits.checkDeposits]
      : data.checkDeposits.checkDeposits;
    setIsClickLoadMore(false);

    return {
      checkDeposits: {
        checkDeposits: checkDepositList,
        pagination: data.checkDeposits.pagination
      }
    };
  };

  const enableCheckDepositBtn = useMemo(
    () => openOnPage && checkIsMobile() && !isWebView,
    [openOnPage, isWebView]
  );

  const {
    loading,
    error: checkDepositError,
    errors,
    data: originalData,
    refetch: refetchCheckDeposits
  } = useQuery(GET_LIST_CHECK_DEPOSIT_QUERY, {
    variables: {
      params: {
        identifier: {
          businessUuid: businessEntityUuid,
          unitAccountId
        },
        filter: {
          status: getStatusPayload(filters[STATUS]),
          month:
            filters[MONTH] === '' || filters[MONTH] === 'all'
              ? undefined
              : getDateRange(filters[MONTH])
        },
        pagination: {
          limit: PAGINATION_DEFAULT_LIMIT,
          offset
        }
      }
    },
    skipCache: true,
    skip: !unitAccountId,
    noThrow: true,
    updateData: updateOriginalTransactions
  });
  useLogError(errors, checkDepositError);

  const data = useMemoObject(originalData);
  useEffect(() => {
    if (checkDepositError) {
      createCommonErrorToast({ error: checkDepositError });
      if (typeof handleClose === 'function') {
        handleClose();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkDepositError]);

  const checkDeposits = useMemo(() => data?.checkDeposits?.checkDeposits ?? [], [data]);

  const hasMore = useMemo(
    () => !!data?.checkDeposits?.pagination?.next && checkDeposits.length > 0,
    [data?.checkDeposits?.pagination?.next, checkDeposits]
  );

  const isHideSkeleton = (data && !loading) || checkDepositError;
  const [showSkeleton, setShowSkeleton] = useSkeleton(isHideSkeleton);

  const handleFilterCommon = useCallback(() => {
    setOffset(0);
    setShowSkeleton();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeFilters = (filter, value) => {
    setFilters((prev) => ({
      ...prev,
      [filter]: value
    }));
  };

  const handleDesktopFilterChange = (filter) => (event) => {
    if (!(filters[filter] === '' && event.target.value === 'all')) {
      handleFilterCommon();
    }
    handleChangeFilters(filter, event.target.value);
  };

  const handleMobileFilterChange = (filter) => (key) => {
    if (!(filters[filter] === '' && key === 'all')) {
      handleFilterCommon();
    }
    handleChangeFilters(filter, key);
  };

  const handleLoadMore = useCallback(() => {
    setIsClickLoadMore(true);
    setOffset(offset + PAGINATION_DEFAULT_LIMIT);
  }, [offset]);

  const handleResetFilters = useCallback(() => {
    setFilters(initFilter);
    handleFilterCommon();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderCheckDepositType = useMemo(() => {
    // The transaction is empty when no transaction return and all filters are empty
    const isEmptyPayment = checkDeposits.length === 0 && !filters[STATUS] && !filters[MONTH];

    // The transaction is empty when no transaction return and at least one filter has value
    const isNotFoundTransaction =
      checkDeposits.length === 0 && (!!filters[STATUS] || !!filters[MONTH]);

    let renderType = RENDER_TRANSACTION_TYPE.SHOW_LIST;

    if (isEmptyPayment) {
      renderType = RENDER_TRANSACTION_TYPE.EMPTY;
    }

    if (isNotFoundTransaction) {
      renderType = RENDER_TRANSACTION_TYPE.NOT_FOUND;
    }

    return renderType;
  }, [filters, checkDeposits]);

  const isDisabledFilter =
    renderCheckDepositType === RENDER_TRANSACTION_TYPE.EMPTY || (loading && !isClickLoadMore);

  const listCheckDeposit = useMemo(() => {
    switch (renderCheckDepositType) {
      case RENDER_TRANSACTION_TYPE.EMPTY:
        return <EmptyPayment description="You do not have any transaction history" />;
      case RENDER_TRANSACTION_TYPE.NOT_FOUND:
        return <NoResultTransaction handleReset={handleResetFilters} />;
      default:
        return (
          <ListCheckDeposit
            checkDeposits={checkDeposits}
            handleLoadMore={handleLoadMore}
            hasMore={hasMore}
            isLoading={loading}
          />
        );
    }
  }, [renderCheckDepositType, handleResetFilters, checkDeposits, handleLoadMore, loading, hasMore]);

  const handleCloseMonthFilterMobile = () => setShowMonthFilter(false);
  const handleCloseStatusFilterMobile = () => setShowStatusFilter(false);

  const handleOpenCheckDepositModal = () => {
    openCheckDepositModal({
      actionsKit: {
        submitCheckDepositSuccessHandler: () => {
          if (offset === 0) {
            // page 1 => just refetch
            refetchCheckDeposits();
          } else {
            setOffset(() => 0);
          } // set offset to page 1 with the same filter
        }
      }
    });
  };

  return (
    <DialogContentStyled>
      {openOnPage ? (
        <PageTitle
          appPageName="check_history"
          title="Check History"
          to={FAQ_CHECK_HISTORY_URL}
        />
      ) : (
        <Heading
          size="3xl"
          weight="bold"
        >
          Check History
        </Heading>
      )}
      <Stack
        marginBottom="24px"
        marginTop="32px"
        spacing="16px"
      >
        {isDesktop ? (
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="space-between"
          >
            <Stack
              alignItems="center"
              direction="row"
              spacing="10px"
            >
              <SelectInput
                bordered
                disabled={isDisabledFilter}
                handleChange={handleDesktopFilterChange(MONTH)}
                Id="month"
                label="Month"
                minWidth={100}
                options={monthOptions}
                value={filters[MONTH]}
              />
              <SelectInput
                bordered
                disabled={isDisabledFilter}
                handleChange={handleDesktopFilterChange(STATUS)}
                Id="type"
                label="Status"
                minWidth={100}
                options={checkDepositStatusOptions}
                value={filters[STATUS]}
              />
            </Stack>
          </Stack>
        ) : (
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              spacing="8px"
            >
              <Button
                disabled={isDisabledFilter}
                endIcon={
                  <SharedAsset
                    alt=""
                    src="/s2/icon/arrow-sm-down-outline.svg"
                  />
                }
                onClick={() => {
                  setShowMonthFilter(true);
                }}
                sx={OptionButtonStyle}
                variant="outlined"
              >
                {getOptionName(filters[MONTH], monthOptions) ?? 'Month'}
              </Button>
              <Button
                disabled={isDisabledFilter}
                endIcon={
                  <SharedAsset
                    alt=""
                    src="/s2/icon/arrow-sm-down-outline.svg"
                  />
                }
                onClick={() => {
                  setShowStatusFilter(true);
                }}
                sx={OptionButtonStyle}
                variant="outlined"
              >
                {getOptionName(filters[STATUS], checkDepositStatusOptions) ?? 'Status'}
              </Button>
            </Stack>
            {enableCheckDepositBtn && (
              <Button
                icon={
                  <SharedAsset
                    alt=""
                    src="/s2/icon/camera-outline.svg"
                    width={16}
                  />
                }
                onClick={handleOpenCheckDepositModal}
                size="small"
                sx={OptionButtonStyle}
                variant="outlined"
              >
                Check Deposit
              </Button>
            )}
          </Stack>
        )}
      </Stack>
      {showSkeleton ? <TransactionSkeleton /> : listCheckDeposit}
      {showMonthFilter && (
        <MobileDrawer handleClose={handleCloseMonthFilterMobile}>
          <FilterOptions
            callback={handleCloseMonthFilterMobile}
            handleClick={handleMobileFilterChange(MONTH)}
            options={monthOptions}
            selectedKey={filters[MONTH]}
          />
        </MobileDrawer>
      )}
      {showStatusFilter && (
        <MobileDrawer handleClose={handleCloseStatusFilterMobile}>
          <FilterOptions
            callback={handleCloseStatusFilterMobile}
            handleClick={handleMobileFilterChange(STATUS)}
            options={checkDepositStatusOptions}
            selectedKey={filters[STATUS]}
          />
        </MobileDrawer>
      )}
    </DialogContentStyled>
  );
}

CheckDepositContainer.propTypes = {
  handleClose: PropTypes.func,
  openOnPage: PropTypes.bool
};
